import { DeleteOutlined, FilterOutlined, MenuOutlined, PlusCircleFilled } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CONSTANT } from '@salescore/client-base'
import type { NodePropertyName, ViewConfigSheet, ViewConfigSorter } from '@salescore/core'
import type { ModalFuncProps } from 'antd'
import { Button, message, Row, Select, Space, Table, Tag } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import {
  createContext,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { v4 } from 'uuid'

import { useGetModel, useGetModelProperty } from '../../../../recoil/view/selectors/connectionsSelector'
import { useGetConfigNode, useViewConfigSheet } from '../../../../recoil/viewConfig'
import { reorderById } from './reorderById'

export const buildViewQuerySortersModalProperties = ({
  hasChanges,
  onClickApply,
}: {
  hasChanges: boolean
  onClickApply: (sorters: ViewConfigSorter[]) => void
}): ModalFuncProps => ({
  icon: null,
  width: '90%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  style: {
    maxWidth: 1000,
  },
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  footer: null,
  content: <ViewQuerySorters hasChanges={hasChanges} onClickApply={onClickApply} />,
})

interface Option {
  value: string
  // 参照列は「列名 + 枠付きバッジ（表示名/ID）」で並び基準を示す。バッジを optionRender に置くと閉じた Select に
  // 反映されず選択後に消えるため、label（ReactNode）自体に埋めて optionLabelProp 既定でドロップダウン・選択後の両方に出す。
  label: ReactNode
  nodeProperty: NodePropertyName
  sortBy: 'value' | 'label'
}

// 並び基準（表示名/ID）を列名と視覚的に区別するための枠付きバッジ。
const sortByTag = (text: string): ReactElement => <Tag style={{ marginInlineStart: 8 }}>{text}</Tag>

type ViewConfigSorterForForm = Partial<ViewConfigSorter>

interface SorterRow {
  id: string
  value: ViewConfigSorterForForm
}

function nodePropertyToKey(nodeProperty: NodePropertyName) {
  return [nodeProperty.nodeName, nodeProperty.modelName, nodeProperty.propertyName].join(',')
}

const useGetNodePropertyLabel = (config: ViewConfigSheet): ((nodeProperty: NodePropertyName) => string) => {
  const getModelProperty = useGetModelProperty()
  const getModel = useGetModel()
  const getConfigNode = useGetConfigNode(config)

  return useCallback(
    (nodeProperty: NodePropertyName) => {
      const node = getConfigNode(nodeProperty.nodeName)
      const model = getModel(nodeProperty.modelName)
      const property = getModelProperty(nodeProperty.modelName, nodeProperty.propertyName)
      return [
        node?.ui?.label ?? model?.label ?? model?.name ?? nodeProperty.nodeName,
        property?.label ?? nodeProperty.propertyName,
      ].join(': ')
    },
    [getConfigNode, getModel, getModelProperty],
  )
}

interface SortableRowContextValue {
  setActivatorNodeRef: (node: HTMLElement | null) => void
  attributes: ReturnType<typeof useSortable>['attributes']
  listeners: ReturnType<typeof useSortable>['listeners']
}

const SortableRowContext = createContext<SortableRowContextValue | null>(null)

const SortableRow = ({ children, ...rest }: { children: ReactElement; 'data-row-key': string }): ReactElement => {
  const id = rest['data-row-key']
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <SortableRowContext.Provider value={{ setActivatorNodeRef, attributes, listeners }}>
      <tr {...rest} ref={setNodeRef} style={style}>
        {children}
      </tr>
    </SortableRowContext.Provider>
  )
}

const SortableHandle = (): ReactElement => {
  const ctx = useContext(SortableRowContext)
  if (ctx === null) {
    return <MenuOutlined style={{ cursor: 'grab' }} />
  }
  // @dnd-kit/sortable の `useSortable` が返す `setActivatorNodeRef` は ref callback、`attributes` は ARIA / role 属性、
  // `listeners` は pointer イベントハンドラ集で、いずれも React の ref ではない（callback ref / 通常の props）。
  // react-hooks/refs は context 経由で展開された三者を ref と誤判定するため、その行のみ disable する。
  return (
    <span
      // eslint-disable-next-line react-hooks/refs
      ref={ctx.setActivatorNodeRef}
      // eslint-disable-next-line react-hooks/refs
      {...ctx.attributes}
      // eslint-disable-next-line react-hooks/refs
      {...ctx.listeners}
      style={{
        cursor: 'grab',
        touchAction: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: 32,
      }}
    >
      <MenuOutlined />
    </span>
  )
}

const components = {
  body: {
    row: SortableRow,
  },
}

const ViewQuerySorters = ({
  hasChanges,
  onClickApply,
}: {
  hasChanges: boolean
  onClickApply: (sorters: ViewConfigSorter[]) => void
}): ReactElement => {
  useEffect(() => {
    if (hasChanges) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [hasChanges])

  const config = useViewConfigSheet()
  const getNodePropertyLabel = useGetNodePropertyLabel(config)
  const getModelProperty = useGetModelProperty()
  const fields = config.fields ?? []
  // option.label は表示名/ID バッジを埋めるため ReactNode。この項目 Select は showSearch を持たないので
  // 文字列前提の絞り込み（optionFilterProp）は働かず、ReactNode label が検索を壊すことはない。
  // 将来 showSearch を足す場合は、ReactNode label ではテキスト照合できないため optionFilterProp の指定が要る。
  const options: Option[] = fields.flatMap((field) => {
    const baseLabel = getNodePropertyLabel(field.property)
    const modelProperty = getModelProperty(field.property.modelName, field.property.propertyName)
    const sortByOptions: Array<{ label: ReactNode; sortBy: 'value' | 'label' }> =
      (modelProperty?.referenceTo ?? []).length > 0
        ? [
            {
              label: (
                <>
                  {baseLabel}
                  {sortByTag(t(`表示名`))}
                </>
              ),
              sortBy: 'label',
            },
            {
              label: (
                <>
                  {baseLabel}
                  {sortByTag(t(`ID`))}
                </>
              ),
              sortBy: 'value',
            },
          ]
        : [{ label: baseLabel, sortBy: 'value' }]
    return sortByOptions.map(({ label, sortBy }): Option => ({
      value: `${nodePropertyToKey(field.property)}:${sortBy}`,
      label,
      nodeProperty: field.property,
      sortBy,
    }))
  })

  const [sorters, setSorters] = useState<readonly SorterRow[]>(() =>
    (config.sorters ?? []).map((s) => ({ id: v4(), value: s })),
  )

  const apply = (): void => {
    const newSorters = sorters
      .map(({ value: x }): ViewConfigSorter | undefined => {
        if (x.property === undefined) {
          return undefined
        }
        // sortBy は本 MR で追加した永続キー。sorter を明示的に再構築するため、この新 FE の保存経路では
        // 必ず sortBy が書かれる。取りこぼしが起きるのは新 FE 以外の経路で、いずれも受容する:
        // (1) CloudFront soak 中に旧 FE バンドルが同じ sorter を再保存すると、旧 FE は sortBy を知らず
        //     再構築時に落とす。旧バンドルは遡及修正できないため恒久策（未知キーを spread で温存する保存）は別途扱う。
        // (2) 新 FE を旧 backend より先に出すと、旧 backend の ViewConfig scalar が safeParse で未知キー sortBy を
        //     strip する。これは backend 先行デプロイで構造的に塞ぐ（デプロイ順は MR 本文参照）。
        return {
          property: x.property,
          order: x.order ?? 'asc',
          type: 'property',
          sortBy: x.sortBy ?? 'value',
        }
      })
      .filter(isNotNullish)
    onClickApply(newSorters)
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }))

  const onDragEnd = ({ active, over }: DragEndEvent): void => {
    if (over === null) {
      return
    }
    setSorters((xs) => reorderById(xs, String(active.id), String(over.id)))
  }

  const columns = [
    {
      key: 'handle',
      title: '',
      width: 48,
      onCell: () => ({ style: { padding: 0 } }),
      render: (): ReactElement => <SortableHandle />,
    },
    {
      key: 'property',
      title: t(`並び替え項目`),
      render: (_: unknown, row: SorterRow): ReactElement => (
        <Select
          style={{ width: '100%' }}
          value={
            row.value.property === undefined
              ? ''
              : `${nodePropertyToKey(row.value.property)}:${row.value.sortBy ?? 'value'}`
          }
          options={options}
          onChange={(value) => {
            const option = options.find((x) => x.value === value)
            if (option !== undefined) {
              setSorters((xs) =>
                xs.map((r) =>
                  r.id === row.id
                    ? { ...r, value: { ...r.value, property: option.nodeProperty, sortBy: option.sortBy } }
                    : r,
                ),
              )
            }
          }}
        />
      ),
    },
    {
      key: 'order',
      title: t(`並び順`),
      width: 140,
      render: (_: unknown, row: SorterRow): ReactElement => (
        <Select
          style={{ width: 100 }}
          value={row.value.order}
          onChange={(order) => {
            setSorters((xs) => xs.map((r) => (r.id === row.id ? { ...r, value: { ...r.value, order } } : r)))
          }}
          options={[
            { label: t(`昇順`), value: 'asc' },
            { label: t(`降順`), value: 'desc' },
          ]}
        />
      ),
    },
    {
      key: 'remove',
      title: '',
      width: 48,
      render: (_: unknown, row: SorterRow): ReactElement => (
        <Button
          icon={<DeleteOutlined />}
          type="text"
          onClick={() => {
            setSorters((xs) => xs.filter((r) => r.id !== row.id))
          }}
        />
      ),
    },
  ]

  return (
    <PageHeader
      title={
        <Space>
          <FilterOutlined />
          <span>{t(`並び替え`)}</span>
          <span style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, fontWeight: 'normal' }}>{t(`(上から優先)`)}</span>
        </Space>
      }
      extra={[]}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sorters.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <Table
            components={components}
            rowKey="id"
            dataSource={sorters}
            columns={columns}
            pagination={false}
            showHeader
          />
        </SortableContext>
      </DndContext>
      <Space className="">
        <Button
          onClick={() => {
            setSorters((xs) => [...xs, { id: v4(), value: { order: 'asc' } }])
          }}
          block
          icon={<PlusCircleFilled />}
          type="text"
          style={{
            color: CONSTANT.colors.primaryColor,
            paddingLeft: 0,
          }}
        >
          {t(`並び替えを追加`)}
        </Button>
      </Space>
      <Row justify="end">
        <Button
          onClick={() => {
            apply()
          }}
        >
          {t(`適用`)}
        </Button>
      </Row>
    </PageHeader>
  )
}
